/**
 * GuideChat — the Guide, as a real conversation.
 *
 * The same chat objects every agent on the platform uses — `MessageItem` for
 * the bubbles and the streaming indicator, `ChatInput` for the typing bar —
 * fitted into the narrow column of the Learn page. Streaming, history and
 * cost go through the same server pipeline as the Concierge; what is
 * particular to the Guide is one event, `tutorial`, which its only tool
 * publishes when it puts a film on the screen. The page owns the player;
 * this component just says "show this one" and drops a marker into the
 * timeline so the chat itself records what was opened.
 *
 * The conversation row is created on the first send, never on mount, so a
 * visit that never asks anything leaves nothing behind.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { MessageItem } from '@modules/conversations/components/MessageItem';
import { ChatInput } from '@modules/conversations/components/ChatInput';
import type { Message } from '@/types/models';
import type { LearnTopic } from '../content/catalog';
import { guideApi } from '../services/guideApi';
import { streamGuideMessage } from '../services/guideStream';
import { useSupportConversation } from '@/components/layout/supportConversation';

type TimelineItem =
  | { kind: 'user'; id: string; text: string; ts: string }
  | { kind: 'assistant'; id: string; text: string; ts: string; cutOff?: boolean; cutOffReason?: string }
  | { kind: 'tutorial'; id: string; topicId: string; why: string | null };

interface Props {
  /** The tutorials this person can see. Used to render the Guide's markers;
   *  the server derives its OWN copy of this list for the prompt and the
   *  tool, so nothing here decides what the Guide may open. */
  topics: LearnTopic[];
  lang: 'en' | 'he';
  /** The Guide opened a tutorial: select it and play it. */
  onShowTutorial: (topicId: string, why: string | null) => void;
  /** A marker in the chat was clicked: bring that tutorial back. */
  onRevisitTutorial: (topicId: string) => void;
  /** Fold the Guide away to a slim rail (the page owns the state). */
  onCollapse?: () => void;
}

/** Openers, so an empty chat is not a blank box. Interface strings like any
 *  other — they are the Guide's own words, not catalog content, so they live
 *  in the locale files rather than inline. */
const STARTER_KEYS = ['learn.starters.model', 'learn.starters.documents', 'learn.starters.settings'];

let _idSeq = 0;
const nextId = () => `g-${Date.now()}-${_idSeq++}`;

export function GuideChat({ topics, lang, onShowTutorial, onRevisitTutorial, onCollapse }: Props) {
  const { t } = useLanguage();
  const [conversationId, setConversationId] = useState<string | null>(null);
  useSupportConversation(conversationId);   // a support report sent from here names the Guide
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);

  // Steady typewriter reveal — the same word-boundary animation the regular
  // chat and the Concierge use, so a Guide reply streams like every other
  // agent's instead of landing in bursts.
  const streamAnimRef = useRef<{
    target: string;
    displayed: string;
    itemId: string;
    raf: number | null;
    lastTime: number;
  } | null>(null);

  const stopStreamAnim = useCallback(() => {
    const anim = streamAnimRef.current;
    if (anim && anim.raf !== null) cancelAnimationFrame(anim.raf);
    streamAnimRef.current = null;
  }, []);

  const tickStreamAnim = useCallback((now: number) => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    const elapsed = now - anim.lastTime;
    anim.lastTime = now;
    if (anim.target.length - anim.displayed.length <= 0) {
      anim.raf = null;
      return;
    }
    const budget = Math.max(1, Math.round(0.15 * elapsed));
    let end = anim.displayed.length + budget;
    if (end < anim.target.length) {
      while (end < anim.target.length && anim.target[end] !== ' ' && anim.target[end] !== '\n') end++;
    }
    anim.displayed = anim.target.slice(0, Math.min(end, anim.target.length));
    const snapshot = anim.displayed;
    const id = anim.itemId;
    setTimeline((prev) => prev.map((it) => (it.kind === 'assistant' && it.id === id ? { ...it, text: snapshot } : it)));
    anim.raf = requestAnimationFrame(tickStreamAnim);
  }, []);

  const startStreamAnim = useCallback((itemId: string) => {
    stopStreamAnim();
    streamAnimRef.current = { target: '', displayed: '', itemId, raf: null, lastTime: 0 };
  }, [stopStreamAnim]);

  const pushStreamText = useCallback((text: string) => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    anim.target = text;
    if (anim.raf === null) {
      anim.lastTime = performance.now();
      anim.raf = requestAnimationFrame(tickStreamAnim);
    }
  }, [tickStreamAnim]);

  const flushStreamAnim = useCallback(() => {
    const anim = streamAnimRef.current;
    if (!anim) return;
    if (anim.raf !== null) cancelAnimationFrame(anim.raf);
    anim.raf = null;
    if (anim.displayed !== anim.target) {
      const id = anim.itemId;
      const full = anim.target;
      setTimeline((prev) => prev.map((it) => (it.kind === 'assistant' && it.id === id ? { ...it, text: full } : it)));
      anim.displayed = anim.target;
    }
  }, []);

  useEffect(() => stopStreamAnim, [stopStreamAnim]);

  const lastText = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      const it = timeline[i];
      if (it.kind !== 'tutorial') return it.text.length;
    }
    return 0;
  }, [timeline]);
  useEffect(() => {
    if (!stickToBottomRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => { el.scrollTop = el.scrollHeight; });
  }, [timeline.length, lastText]);

  const onScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const fail = useCallback((assistantId: string) => {
    flushStreamAnim();
    setTimeline((prev) =>
      prev.map((it) =>
        it.kind === 'assistant' && it.id === assistantId && !it.text
          ? { ...it, text: t('learn.guideUnavailable') }
          : it,
      ),
    );
  }, [flushStreamAnim, t]);

  const send = useCallback(
    async (raw: string) => {
      const text = (raw || '').trim();
      if (!text || busy) return;

      setBusy(true);
      stickToBottomRef.current = true;
      const ts = new Date().toISOString();
      const userId = nextId();
      const assistantId = nextId();
      setTimeline((prev) => [
        ...prev,
        { kind: 'user', id: userId, text, ts },
        { kind: 'assistant', id: assistantId, text: '', ts },
      ]);
      startStreamAnim(assistantId);
      let streamed = '';

      try {
        let cid = conversationId;
        if (!cid) {
          const conv = await guideApi.createConversation(lang);
          cid = conv.conversation_id;
          setConversationId(cid);
        }
        for await (const ev of streamGuideMessage(cid, { message: text })) {
          if (ev.type === 'ai_chunk') {
            streamed += ev.content || '';
            pushStreamText(streamed);
          } else if (ev.type === 'tutorial') {
            const tut = ev;
            // The marker sits ABOVE the reply that introduced it — the tool
            // runs before the model writes, which is also the order on screen.
            setTimeline((prev) => {
              const idx = prev.findIndex((it) => it.kind === 'assistant' && it.id === assistantId);
              const marker: TimelineItem = { kind: 'tutorial', id: nextId(), topicId: tut.id, why: tut.why ?? null };
              if (idx < 0) return [...prev, marker];
              return [...prev.slice(0, idx), marker, ...prev.slice(idx)];
            });
            onShowTutorial(tut.id, tut.why ?? null);
          } else if (ev.type === 'ai_complete') {
            const msg = (ev as { message?: { message_content?: string;
                                             message_metadata?: { answer_cut_off?: boolean;
                                                                  answer_cut_off_reason?: string } } }).message;
            const finalText = msg?.message_content;
            if (typeof finalText === 'string' && finalText) {
              streamed = finalText;
              pushStreamText(streamed);
            }
            // The output cap paused it: MessageItem shows the same note as every
            // chat — and, with no text at all, says so instead of "Thinking…" forever.
            if (msg?.message_metadata?.answer_cut_off === true) {
              setTimeline((prev) => prev.map((it) => (
                it.kind === 'assistant' && it.id === assistantId
                ? { ...it, cutOff: true, cutOffReason: msg.message_metadata?.answer_cut_off_reason } : it)));
            }
          } else if (ev.type === 'error') {
            fail(assistantId);
          }
        }
      } catch {
        fail(assistantId);
      } finally {
        setBusy(false);
      }
    },
    [busy, conversationId, lang, startStreamAnim, pushStreamText, onShowTutorial, fail],
  );

  const lastAssistantId = useMemo(() => {
    for (let i = timeline.length - 1; i >= 0; i--) {
      if (timeline[i].kind === 'assistant') return timeline[i].id;
    }
    return undefined;
  }, [timeline]);

  const isEmpty = timeline.length === 0;

  // The Guide's face on every answer: its question mark, not the tenant's
  // logo — it is the platform's helper, not one of the tenant's agents. One
  // stable element, so MessageItem's memo does not re-render on each tick.
  const guideMark = useMemo(
    () => <span className="lp-guide-mark lp-guide-avatar" aria-hidden>?</span>,
    [],
  );

  return (
    <div className="lp-guide" data-testid="guide-chat">
      <header className="lp-guide-head">
        <span className="lp-guide-mark" aria-hidden>?</span>
        <span className="lp-guide-titles">
          <span className="lp-guide-title">{t('learn.guideTitle')}</span>
          <span className="lp-guide-sub">{t('learn.guideSub')}</span>
        </span>
        {onCollapse && (
          <button
            className="lp-guide-collapse"
            onClick={onCollapse}
            aria-label={t('learn.hideGuide')}
            title={t('learn.hideGuide')}
            data-testid="guide-collapse"
          >
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        )}
      </header>

      <div className="chat-messages-container lp-guide-scroll" ref={scrollRef} onScroll={onScroll}>
        <div className="chat-messages lp-guide-messages">
          {isEmpty && (
            <div className="lp-guide-empty">
              <p className="lp-guide-intro">{t('learn.guideIntro')}</p>
              <div className="lp-guide-starters">
                {STARTER_KEYS.map((key) => (
                  <button key={key} className="lp-guide-starter" onClick={() => void send(t(key))} data-testid="guide-starter">
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}

          {timeline.map((item) => {
            if (item.kind === 'tutorial') {
              const topic = topics.find((x) => x.id === item.topicId);
              if (!topic) return null;
              return (
                <button
                  key={item.id}
                  className={`lp-guide-tutorial ${topic.film ? 'has-film' : ''}`}
                  onClick={() => onRevisitTutorial(item.topicId)}
                  data-testid="guide-tutorial-marker"
                >
                  <span className="lp-guide-tutorial-kind">
                    {t(topic.film ? 'learn.nowShowing' : 'learn.nowReading')}
                  </span>
                  <span className="lp-guide-tutorial-title" dir="auto">{topic.title[lang]}</span>
                  {item.why && <span className="lp-guide-tutorial-why" dir="auto">{item.why}</span>}
                </button>
              );
            }
            const message: Message = {
              message_id: item.id,
              conversation_id: conversationId || 'guide',
              role: item.kind,
              content: item.text,
              created_at: item.ts,
              render_key: item.id,
              answer_cut_off: item.kind === 'assistant' && item.cutOff === true,
              answer_cut_off_reason: item.kind === 'assistant' ? item.cutOffReason : undefined,
            };
            return (
              <MessageItem
                key={item.id}
                message={message}
                avatar={guideMark}
                isStreaming={busy && item.id === lastAssistantId && !item.text}
              />
            );
          })}
        </div>
      </div>

      <div className="lp-guide-composer">
        <ChatInput
          onSendMessage={(content: string) => void send(content)}
          selectedModel=""
          onModelChange={() => undefined}
          disableModelPicker
          disableAttachments
          journeyDetection={false}
          sendDisabled={busy}
          isStreaming={busy}
        />
      </div>
    </div>
  );
}
